import {
    Card,
    CheckboxGroup,
    Colors,
    FOCUS_RING,
    Heading,
    Icon,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';
import { useEffect, useRef } from 'react';

import type { ScheduleCreationFormFields } from '../../validation';
import type { TranslationKey } from '@/i18n-key';

import { useAmelioTranslations } from '@/hooks';
import {
    isReminderOffsetWithinCompletionWindow,
    OFFERED_REMINDER_DAYS_BEFORE_DEADLINE,
} from '@/utils/schedule';

interface AutomatedRemindersProps {
    reminderSevenDays: boolean;
    reminderThreeDays: boolean;
    reminderOnDeadline: boolean;
    completionWindowDays?: number;
    onReminderChange: (params: {
        field: keyof typeof ScheduleCreationFormFields;
        value: boolean;
    }) => void;
}

const LABEL_KEY_BY_DAYS: Readonly<Record<number, TranslationKey>> = {
    7: 'evaluation360ScheduleCreation.automatedReminders.sevenDaysBefore',
    3: 'evaluation360ScheduleCreation.automatedReminders.treeDaysBefore',
    0: 'evaluation360ScheduleCreation.automatedReminders.onDeadlineDay',
};

const FIELD_BY_DAYS: Readonly<Record<number, keyof typeof ScheduleCreationFormFields>> = {
    7: 'reminderSevenDays',
    3: 'reminderThreeDays',
    0: 'reminderOnDeadline',
};

const AutomatedReminders = ({
    reminderSevenDays,
    reminderThreeDays,
    reminderOnDeadline,
    completionWindowDays,
    onReminderChange,
}: AutomatedRemindersProps) => {
    const { t } = useAmelioTranslations();
    const isFirstRender = useRef(true);

    /**
     * The input guard only blocks turning a closed delay ON. This closes the opposite direction — a
     * delay already ON when the window shrinks under it — instead of leaving that for the save-time
     * refusal. Skipping the first render keeps a schedule already loaded outside the window
     * surfaced through the tooltip below for the manager to turn off, not silently cleared.
     */
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        const isCheckedByDays: Readonly<Record<number, boolean>> = {
            7: reminderSevenDays,
            3: reminderThreeDays,
            0: reminderOnDeadline,
        };

        OFFERED_REMINDER_DAYS_BEFORE_DEADLINE.filter(
            (days) =>
                isCheckedByDays[days] &&
                !isReminderOffsetWithinCompletionWindow({
                    daysBeforeDeadline: days,
                    daysAllowedToComplete: completionWindowDays,
                }),
        ).forEach((days) => onReminderChange({ field: FIELD_BY_DAYS[days], value: false }));
    }, [
        completionWindowDays,
        reminderSevenDays,
        reminderThreeDays,
        reminderOnDeadline,
        onReminderChange,
    ]);

    const selectedValues = [
        reminderSevenDays && '7',
        reminderThreeDays && '3',
        reminderOnDeadline && '0',
    ].filter(Boolean) as string[];

    const isDelayClosed = (daysBeforeDeadline: number): boolean =>
        !isReminderOffsetWithinCompletionWindow({
            daysBeforeDeadline,
            daysAllowedToComplete: completionWindowDays,
        });

    const blocksTurningTheDelayOn = (daysBeforeDeadline: number): boolean =>
        isDelayClosed(daysBeforeDeadline) && !selectedValues.includes(String(daysBeforeDeadline));

    const closedDelayReason = t(
        'evaluation360ScheduleCreation.automatedReminders.outsideWindowUnavailable',
        { window: completionWindowDays },
    );

    const hasClosedDelay = OFFERED_REMINDER_DAYS_BEFORE_DEADLINE.some(isDelayClosed);

    const handleChange = (values: string[]) => {
        onReminderChange({ field: 'reminderSevenDays', value: values.includes('7') });
        onReminderChange({ field: 'reminderThreeDays', value: values.includes('3') });
        onReminderChange({ field: 'reminderOnDeadline', value: values.includes('0') });
    };

    return (
        <Card
            className={'mt-12'}
            bgColor={'bg-white'}
            borderRadius={'2xl'}
            padding={'xl'}
        >
            <div>
                <div className={'flex items-center'}>
                    <Icon
                        name={'notifications-notification'}
                        size={'xl'}
                        className={'mr-3'}
                    />

                    <Heading level={5}>
                        {t('evaluation360ScheduleCreation.automatedReminders.title')}
                    </Heading>
                </div>

                <Text
                    className={'mt-1'}
                    color={Colors.BASE_400}
                    weight={'medium'}
                    size={'small'}
                >
                    {t('evaluation360ScheduleCreation.automatedReminders.description')}
                </Text>
            </div>

            <div className={'mt-7 flex w-full items-center'}>
                <CheckboxGroup
                    name={'automated-reminders'}
                    orientation={'horizontal'}
                    spacing={'loose'}
                    values={selectedValues}
                    onChange={handleChange}
                    options={OFFERED_REMINDER_DAYS_BEFORE_DEADLINE.map((days) => ({
                        label: t(LABEL_KEY_BY_DAYS[days]),
                        value: String(days),
                        disabled: blocksTurningTheDelayOn(days),
                    }))}
                    dataTestId={'automated-reminders'}
                />

                {hasClosedDelay && (
                    <TooltipProvider delayDuration={200}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type={'button'}
                                    className={`ml-3 inline-flex ${FOCUS_RING}`}
                                    aria-label={closedDelayReason}
                                    data-testid={'automated-reminders-closed-reason-trigger'}
                                >
                                    <Icon
                                        name={'essential-info-circle'}
                                        size={'sm'}
                                        color={Colors.BASE_400}
                                    />
                                </button>
                            </TooltipTrigger>

                            <TooltipContent
                                side={'top'}
                                className={'max-w-xs'}
                                data-testid={'automated-reminders-closed-reason'}
                            >
                                <p>{closedDelayReason}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )}
            </div>
        </Card>
    );
};

export { AutomatedReminders };
export type { AutomatedRemindersProps };
